<template>
  <Navbar />
  <div class="body password-container auth-page-skin auth-visual-layout" :dir="direction">

    <div class="password-card auth-panel">
      <h2>{{ $t('SetPassword.passwordSetupTitle') }}</h2>
      <p class="subtitle">{{ $t('SetPassword.subtitle') }}</p>

      <form @submit.prevent="handleSetPassword">
        <div class="form-group">
          <label class="form-label">{{ $t('SetPassword.password') }}</label>

          <input 
            v-model="password"
            type="password" 
            class="form-input" 
            :placeholder="$t('SetPassword.passwordPlaceholder')"
            @input="checkPasswordStrength"
          />

          <div class="password-strength">
            <div class="strength-bar" :style="strengthBarStyle"></div>
            <div class="strength-text">{{ strengthText }}</div>
          </div>

          <div class="password-requirements">
            <p :class="lengthValid ? 'valid' : 'invalid'">
              {{ $t('SetPassword.lengthRequirement') }}
            </p>

            <p :class="letterValid ? 'valid' : 'invalid'">
              {{ $t('SetPassword.letterRequirement') }}
            </p>

            <p :class="numberValid ? 'valid' : 'invalid'">
              {{ $t('SetPassword.numberRequirement') }}
            </p>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('SetPassword.confirmPassword') }}</label>

          <input 
            v-model="confirmPassword"
            type="password" 
            class="form-input" 
            :placeholder="$t('SetPassword.confirmPasswordPlaceholder')"
            @input="checkPasswordMatch"
          />

          <div class="confirm-message" :class="passwordMatch ? 'valid' : 'invalid'">
            {{ confirmMessage }}
          </div>
        </div>

        <button 
          type="submit" 
          class="submit-btn" 
          :disabled="!isFormValid || loading"
        >
          {{ loading ? 'Loading...' : $t('SetPassword.completeRegistration') }}
        </button>
      </form>
    </div>
  

      <div class="register-image-col" aria-hidden="true">
        <div class="visual">
          <div class="hero-orbit">
            <img class="visual-image" src="@/assets/register-login-bg.svg" alt="" />
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { registerService } from '@/services/api'
import Navbar from '@/components/navbar_styled_register.vue'

export default {
  name: 'SetPasswordPage',
  components: {
    Navbar,
  },

  setup() {
    const router = useRouter()
    const { t, locale } = useI18n()

    const phoneNumber = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const loading = ref(false)

    const currentLang = ref('fa')

    // Password validations
    const lengthValid = ref(false)
    const letterValid = ref(false)
    const numberValid = ref(false)
    const passwordMatch = ref(false)

    const direction = computed(() => {
      return ['fa', 'ar'].includes(locale.value) ? 'ltr' : 'rtl'
    })

    const strength = computed(() => {
      let score = 0
      if (lengthValid.value) score++
      if (letterValid.value) score++
      if (numberValid.value) score++
      return score
    })

    const strengthText = computed(() => {
      if (password.value.length === 0) return ''

      switch (strength.value) {
        case 1: return t('SetPassword.strengthWeak')
        case 2: return t('SetPassword.strengthMedium')
        case 3: return t('SetPassword.strengthStrong')
        default: return ''
      }
    })

    const strengthBarStyle = computed(() => {
      const width = (strength.value / 3) * 100
      let color = '#e5e7eb'

      if (password.value.length > 0) {
        switch (strength.value) {
          case 1: color = '#dc2626'; break   // red
          case 2: color = '#f59e0b'; break   // yellow
          case 3: color = '#059669'; break   // green
        }
      }

      return {
        width: `${width}%`,
        backgroundColor: color
      }
    })

    const confirmMessage = computed(() => {
      if (confirmPassword.value.length === 0) return ''
      return passwordMatch.value 
        ? t('SetPassword.passwordMatch') 
        : t('SetPassword.passwordMismatch')
    })

    const isFormValid = computed(() => {
      return strength.value === 3 && passwordMatch.value
    })

    const checkPasswordStrength = () => {
      const pwd = password.value
      lengthValid.value = pwd.length >= 8
      letterValid.value = /[a-zA-Zآ-ی]/.test(pwd)
      numberValid.value = /[0-9]/.test(pwd)

      checkPasswordMatch()
    }

    const checkPasswordMatch = () => {
      passwordMatch.value =
        password.value === confirmPassword.value &&
        password.value.length > 0
    }

    const switchLanguage = () => {
      localStorage.setItem('selectedLanguage', locale.value)
    }

    const handleSetPassword = async () => {
      if (!isFormValid.value) return
      loading.value = true

      try {
        const response = await registerService.setPassword(
          phoneNumber.value, 
          password.value
        )

        if (response.data.success) {
          alert(t('SetPassword.registrationSuccess'))
          router.push('/login')
        } else {
          throw new Error(response.data.message)
        }

      } catch (err) {
        alert(
          err.response?.data?.message || 
          t('SetPassword.errorGeneral')
        )
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      const savedPhone = localStorage.getItem('pendingPhoneNumber')

      if (!savedPhone) {
        router.push('/register')
        return
      }

      phoneNumber.value = savedPhone

      const savedLang = localStorage.getItem('selectedLanguage') || 'fa'
      currentLang.value = savedLang
      locale.value = savedLang
    })

    return {
      phoneNumber,
      password,
      confirmPassword,
      loading,
      currentLang,

      // i18n
      t,

      direction,
      lengthValid,
      letterValid,
      numberValid,
      passwordMatch,
      strengthText,
      strengthBarStyle,
      confirmMessage,
      isFormValid,

      checkPasswordStrength,
      checkPasswordMatch,
      switchLanguage,
      handleSetPassword
    }
  }
}
</script>

<style scoped>
.password-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f9fafb;
  padding: 1rem;
}

.password-card {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
}

.password-card h2 {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-align: center;
  color: #2563eb;
}

.subtitle {
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 1.5rem;
  text-align: center;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 5px;
  background-color: #e5e7eb;
  border-radius: 3px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
  width: 0%;
}

.strength-text {
  font-size: 0.8rem;
  font-weight: 500;
}

.password-requirements {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  background-color: #f9fafb;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.password-requirements p {
  margin: 0.2rem 0;
  display: flex;
  align-items: center;
}

.password-requirements p:before {
  content: "•";
  margin-left: 0.5rem;
}

.valid {
  color: #059669 !important;
}

.invalid {
  color: #dc2626 !important;
}

.confirm-message {
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.submit-btn {
  width: 100%;
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 0.75rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 1rem;
}

.submit-btn:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.submit-btn:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.language-switcher {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.language-dropdown-select {
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ced4da;
  background-color: #fff;
  font-size: 1rem;
  cursor: pointer;
}

[dir="rtl"] .password-card,
[dir="rtl"] .form-label,
[dir="rtl"] .strength-text,
[dir="rtl"] .confirm-message {
  text-align: right;
}

[dir="ltr"] .password-card,
[dir="ltr"] .form-label,
[dir="ltr"] .strength-text,
[dir="ltr"] .confirm-message {
  text-align: left;
}

[dir="rtl"] .password-requirements p:before {
  margin-left: 0.5rem;
  margin-right: 0;
}

[dir="ltr"] .password-requirements p:before {
  margin-right: 0.5rem;
  margin-left: 0;
}

[dir="rtl"] .language-switcher {
  right: 1rem;
  left: auto;
}

[dir="ltr"] .language-switcher {
  left: 1rem;
  right: auto;
}
.password-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--bg);
  padding: 1rem;
}

/* Card */
.password-card {
  background: var(--card-bg);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: var(--shadow-2);
  width: 100%;
  max-width: 400px;
}

/* Title */
.password-card h2 {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--primary);
}

.subtitle {
  font-size: 0.95rem;
  color: var(--subtext);
  margin-bottom: 1.5rem;
  text-align: center;
}

/* Form */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text);
}

/* Input */
.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--input-border, var(--border));
  border-radius: 6px;
  background: var(--input-bg, var(--card-bg));
  color: var(--text);
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

/* Strength meter */
.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 5px;
  background-color: var(--border);
  border-radius: 3px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
  width: 0%;
}

.strength-text {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--subtext);
}

/* Requirements */
.password-requirements {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  background-color: var(--muted);
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border);
}

.password-requirements p {
  margin: 0.2rem 0;
  display: flex;
  align-items: center;
}

/* Bullets */
.password-requirements p:before {
  content: "•";
}

/* Validations */
.valid {
  color: var(--green) !important;
}

.invalid {
  color: var(--red) !important;
}

.confirm-message {
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
  color: var(--text);
}

/* Submit Button */
.submit-btn {
  width: 100%;
  background-color: var(--primary);
  color: var(--text-light, #fff);
  border: none;
  padding: 0.75rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 1rem;
}

.submit-btn:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.submit-btn:disabled {
  background-color: var(--disabled);
  cursor: not-allowed;
}

/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .auth-panel { position:relative; z-index:2; width:min(100%,440px); margin:0 auto; padding:36px 32px 30px; border:1px solid var(--auth-border); border-radius:22px; background:var(--auth-surface); color:var(--auth-text); box-shadow:0 24px 70px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.04); backdrop-filter:blur(14px); }
.auth-page-skin .auth-panel h2 { color:var(--auth-text); font-size:clamp(22px,3vw,29px); font-weight:800; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }
@media (max-width:767px) { .auth-page-skin { min-height:calc(100svh - 56px); padding:34px 14px 40px; } .auth-page-skin::before { background-size:min(560px,125vw) auto; opacity:.28; } .auth-page-skin .auth-panel { width:min(100%,440px); padding:28px 22px; } }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}
.otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
}
.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }
  .otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
    position: relative;
    z-index: 2;
    width: min(100%, 440px);
    margin: 0 auto;
  }
}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}


/* Use the same real visual column and sizing as Register/Login. */
.auth-page-skin.auth-visual-layout {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: 100dvh !important;
  height: auto !important;
  padding: 72px clamp(24px, 5vw, 88px) 32px;
  box-sizing: border-box;
}
.auth-visual-layout::before,
.auth-visual-layout::after { display: none; }
.auth-visual-layout > .auth-panel { grid-column: 2; grid-row: 1; justify-self: end; }
.auth-visual-layout > .register-image-col { grid-column: 1; grid-row: 1; display: flex; align-items: center; justify-content: center; width: 100%; height: min(72svh, 680px); min-width: 0; overflow: hidden; padding: 0; }
.auth-visual-layout .visual { position: relative; display: block; width: min(100%, 680px); height: 680px; aspect-ratio: 1; overflow: visible; }
.auth-visual-layout .hero-orbit { position: absolute; inset: 0; z-index: 1; overflow: visible; pointer-events: none; }
.auth-visual-layout .visual-image { position: relative; display: block; width: 100%; height: 100%; object-fit: contain; }
@media (max-width: 767px) {
  .auth-page-skin.auth-visual-layout { display: block; min-height: 100dvh !important; padding: 34px 14px 40px; }
  .auth-visual-layout > .auth-panel { position: relative; z-index: 4; width: min(100%, 440px); margin: 0 auto; }
  .auth-visual-layout > .register-image-col { position: absolute; z-index: 0; inset: 0; display: block; width: 100%; height: 100%; pointer-events: none; }
  .auth-visual-layout .visual { position: absolute; top: 50%; left: 50%; width: min(112vw, 440px); height: 440px; transform: translate(-50%, -50%); max-width: none; }
}
</style>
